import React, { useState, useEffect } from 'react';
import {
	Box,
	useTheme,
	useMediaQuery,
	IconButton,
	Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import TrustMarker from '../common/TrustMarker';
import Footer from '../Sections/Footer';
import StepperBar from '../common/Stepper';
import RevokeMessage from '../common/RevokeMessage';
import ConsentRenewCard from './ConsentRenewCard';
import ActionButtons from '../common/ActionButtons';
import DialogBox from '../Dialog Box/Dialog Box';
import DenyConsent from '../Accounts/DenyConsent';
import CustomDrawer from '../Drawer/Drawer';
import AuthApis from '../../hepler/authApis.service';
import {
	startConsentRenewalView,
	captureConsentRenewalApproved,
	captureConsentRenewalDenied,
	captureConsentRenewalPageLoaded,
} from '../../utils/analytics/posthog.utils';
import { useTranslation } from '../../hooks/useTranslation';
import { filterMatchingAccounts } from '../../utils/helper.utils';

const ConsentRenew = (props) => {
	const { t } = useTranslation();
	const theme = useTheme();
	const isMobileOrTablet = useMediaQuery(theme.breakpoints.down('lg'));
	const isMobile = useMediaQuery(theme.breakpoints.down('md'));
	const isDark =
		props.customDetails?.darkTheme === true || theme.palette.mode === 'dark';
	const data = props.location?.state?.data;
	const fiuName = global.entityName || '';

	// Dynamically calculate header height
	const [headerHeight, setHeaderHeight] = useState(70);
	const [openDialog, setOpenDialog] = useState(false);
	const [openDrawer, setOpenDrawer] = useState(false);
	const [trackedAccounts, setTrackedAccounts] = useState(null);
	const [userLinkedAccounts, setUserLinkedAccounts] = useState(null);
	const [consentStatus, setConsentStatus] = useState(null);

	const authApis = new AuthApis();

	const handleClose = () => {
		setOpenDialog(false);
	};

	const handleApprove = async () => {
		try {
			const response = await authApis.consentRequestRenew(
				'ACCEPT',
				global.renewConsentId
			);

			// Capture analytics for consent renewal approval
			if (global?.sdkOptions?.analyticsEnabled) {
				const totalAccountsRequested = trackedAccounts
					? trackedAccounts.length
					: 0;
				const totalLinkedAccounts = userLinkedAccounts
					? userLinkedAccounts.length
					: 0;

				const metrics = {
					totalConsents: 1,
					approvedConsents: 1,
					totalAccountsRequested,
					totalLinkedAccounts,
				};
				captureConsentRenewalApproved(metrics);
			}

			if (response && response?.status === 'SUCCESS') {
				props.history.push({
					pathname: `/consent-renew-status`,
					state: {
						status: 'Consent renewal approved',
						statusCode: 200,
					},
				});
			} else {
				// Navigate to error page
				setTimeout(() => {
					props.history.push({
						pathname: `/consent-renew-status`,
						state: {
							status: 'Consent renewal failed',
							statusCode: 20,
						},
					});
				}, 1000);
			}
		} catch (error) {
			console.error('Error approving consent:', error);
			// Navigate to error page
			setTimeout(() => {
				props.history.push({
					pathname: `/consent-renew-status`,
					state: {
						status: 'Consent renewal failed',
						statusCode: 20,
					},
				});
			}, 1000);
		}
	};

	const handleDeny = async () => {
		try {
			const response = await authApis.consentRequestRenew(
				'DENY',
				global.renewConsentId
			);
			setOpenDialog(false);

			// Capture analytics for consent renewal denial
			if (global?.sdkOptions?.analyticsEnabled) {
				const totalAccountsRequested = trackedAccounts
					? trackedAccounts.length
					: 0;

				const metrics = {
					totalConsents: 1,
					deniedConsents: 1,
					totalAccountsRequested,
					totalLinkedAccounts: 0,
				};
				captureConsentRenewalDenied(metrics);
			}

			if (response && response?.status === 'SUCCESS') {
				// Navigate to denied page
				props.history.push({
					pathname: `/consent-renew-status`,
					state: {
						status: 'Consent renewal denied',
						statusCode: 201,
					},
				});
			} else {
				// Navigate to error page
				setTimeout(() => {
					props.history.push({
						pathname: `/consent-renew-status`,
						state: {
							status: 'Consent renewal failed',
							statusCode: 20,
						},
					});
				}, 1000);
			}
		} catch (error) {
			console.error('Error denying consent:', error);
			// Navigate to error page
			setTimeout(() => {
				props.history.push({
					pathname: `/consent-renew-status`,
					state: {
						status: 'Consent renewal failed',
						statusCode: 20,
					},
				});
			}, 1000);
		}
	};

	const fetchConsentDetails = async () => {
		authApis
			.consentDetails(global.renewConsentId)
			.then((res) => {
				if (res?.consentStatus === 'REVOKED') {
					props.history.push({
						pathname: `/consent-renew-status`,
						state: {
							status: 'Consent already revoked',
							statusCode: 202,
						},
					});
					return;
				}

				if (
					res?.status !== 'SUCCESS' ||
					(res?.consentStatus !== 'ACTIVE' && res?.consentStatus !== 'EXPIRED')
				) {
					props.history.push({
						pathname: `/consent-renew-status`,
						state: {
							status: 'Consent is not active or invalid',
							statusCode: 20,
						},
					});
					return;
				}

				setConsentStatus(res?.consentStatus);
				setTrackedAccounts(res.Accounts);
				getUserLinkedAccounts(res.Accounts);
			})
			.catch((error) => {
				console.error('Error fetching consent details:', error);
				props.history.push({
					pathname: `/consent-renew-status`,
					state: {
						status: 'Failed to fetch consent details',
						statusCode: 20,
					},
				});
			});
	};

	useEffect(() => {
		startConsentRenewalView();
		fetchConsentDetails();
	}, []);

	useEffect(() => {
		const calculateHeaderHeight = () => {
			const appBar = document.querySelector('header.MuiAppBar-root');
			if (appBar) {
				const height = appBar.offsetHeight;
				setHeaderHeight(height);
			}
		};

		calculateHeaderHeight();

		window.addEventListener('resize', calculateHeaderHeight);

		const observer = new MutationObserver(calculateHeaderHeight);
		const appBar = document.querySelector('header.MuiAppBar-root');
		if (appBar) {
			observer.observe(appBar, {
				childList: true,
				subtree: true,
				attributes: true,
			});
		}

		return () => {
			window.removeEventListener('resize', calculateHeaderHeight);
			observer.disconnect();
		};
	}, []);

	const getUserLinkedAccounts = async (consentAccounts) => {
		try {
			const response = await authApis.getLinkedAccounts();
			if (response?.status === 'SUCCESS') {
				const filteredAccounts = filterMatchingAccounts(
					consentAccounts,
					response?.LinkedAccounts
				);
				setUserLinkedAccounts(filteredAccounts);

				// Capture analytics for page load
				if (global?.sdkOptions?.analyticsEnabled) {
					const totalAccountsRequested = consentAccounts
						? consentAccounts.length
						: 0;
					const totalLinkedAccounts = filteredAccounts
						? filteredAccounts.length
						: 0;

					const metrics = {
						totalConsents: 1,
						totalAccountsRequested,
						totalLinkedAccounts,
					};
					captureConsentRenewalPageLoaded(metrics);
				}
			} else {
				props.history.push({
					pathname: `/consent-renew-status`,
					state: {
						status: 'Consent is not active or invalid',
						statusCode: 20,
					},
				});
				return;
			}
		} catch (error) {
			console.error('Error fetching user linked accounts:', error);
			props.history.push({
				pathname: `/consent-renew-status`,
				state: {
					status: 'Consent is not active or invalid',
					statusCode: 20,
				},
			});
			return;
		}
	};

	return (
		<Box
			sx={{
				fontFamily: props?.customDetails?.fontFamily,
				backgroundColor: isDark
					? '#161616'
					: props.customDetails?.secondaryColor || 'transparent',
				minHeight: '100vh',
			}}
		>
			{global.isRedirect === 'true' && (
				<IconButton
					onClick={() => (isMobile ? setOpenDrawer(true) : setOpenDialog(true))}
					disableRipple
					sx={{
						position: 'fixed',
						top: isMobile ? '20px' : '25px',
						right: isMobile ? '15px' : '30px',
						zIndex: 1300,
						p: '0.25rem',
						border: '1px solid #CCCCCC',
						backgroundColor: 'transparent',
						color: isDark ? '#FFFFFF' : '#000000',
						'&:hover': {
							backgroundColor: isDark
								? 'rgba(255, 255, 255, 0.1)'
								: 'rgba(0, 0, 0, 0.04)',
						},
					}}
				>
					<CloseIcon sx={{ fontSize: '0.85rem' }} />
				</IconButton>
			)}

			<Box
				sx={{
					paddingTop: `${headerHeight}px`,
					paddingBottom: isMobileOrTablet ? '225px' : '160px',
				}}
			>
				<Box>
					<TrustMarker customDetails={props.customDetails} />
				</Box>
				<Box
					sx={{
						width: isMobileOrTablet ? '95%' : '83.5%',
						margin: '0 auto',
						'& > div': { paddingTop: '30px !important' },
					}}
				>
					<StepperBar customDetails={props.customDetails} />
				</Box>

				<Box
					sx={{
						width: isMobileOrTablet ? '90%' : '50%',
						margin: '0 auto',
						marginTop: '14px',
						marginBottom: '14px',
					}}
				>
					<Typography
						variant="h6"
						sx={{
							fontSize: { xs: '0.875rem', sm: '0.875rem', md: '1rem' },
							fontWeight: 600,
							fontFamily: props?.customDetails?.fontFamily,
							color: isDark ? '#fff' : '#000',
							textAlign: 'left',
						}}
					>
						{`${t('consentRenewal.titles.reviewConsent')} ${fiuName}`}
					</Typography>
				</Box>

				<Box>
					<ConsentRenewCard
						customDetails={props.customDetails}
						consentData={data && data.length > 0 ? data[0] : null}
						trackedAccounts={userLinkedAccounts}
						consentStatus={consentStatus}
					/>
				</Box>

				<Box sx={{ marginTop: '16px' }}>
					<RevokeMessage
						customDetails={props.customDetails}
						consentStatus={consentStatus}
					/>
				</Box>
			</Box>

			<ActionButtons
				customDetails={props.customDetails}
				onCancel={() => (isMobile ? setOpenDrawer(true) : setOpenDialog(true))}
				onApprove={handleApprove}
			/>

			<Box
				sx={{
					position: 'fixed',
					bottom: 0,
					left: 0,
					right: 0,
					zIndex: 1000,
					'& > div': {
						height: 'auto !important',
					},
				}}
			>
				<Footer {...props} />
			</Box>

			<DialogBox
				open={openDialog}
				handleClose={handleClose}
				title={t('consentRenewal.titles.denyRenewal')}
				subTitle={t('consentRenewal.messages.denyConfirmation').replace(
					'{fiuName}',
					global.entityName || 'the FIU'
				)}
				boxPadding="1.5rem"
				subTitleMaxWidth="85%"
				customDetails={props.customDetails}
				isConsentRenewal={true}
			>
				<DenyConsent
					handleCancel={handleClose}
					{...props}
					handleDeny={handleDeny}
					isDarkTheme={isDark}
					customDetails={props.customDetails}
					denyButtonText={t('common.buttons.yesDontRenew')}
					cancelButtonText={t('common.buttons.noGoBack')}
					isConsentRenewal={true}
				/>
			</DialogBox>

			<CustomDrawer
				open={openDrawer}
				setOpen={setOpenDrawer}
				anchor="bottom"
				height="auto"
				customDetails={props.customDetails}
				showCloseBtn={false}
			>
				<Box
					sx={{
						padding: '1.5rem 1.5rem 1.5rem 1.5rem',
						fontFamily: props?.customDetails?.fontFamily,
						backgroundColor: isDark
							? '#161616'
							: props.customDetails?.secondaryColor || '#fff',
					}}
				>
					<Typography
						sx={{
							fontSize: '1rem',
							fontWeight: 600,
							fontFamily: props?.customDetails?.fontFamily,
							color: isDark ? '#fff' : '#000',
							marginBottom: '0.5rem',
						}}
					>
						{t('consentRenewal.titles.denyRenewal')}
					</Typography>
					<Typography
						sx={{
							fontSize: '0.875rem',
							fontFamily: props?.customDetails?.fontFamily,
							color: isDark ? '#B0B0B0' : '#666',
							marginBottom: '2rem',
						}}
					>
						{t('consentRenewal.messages.denyConfirmation').replace(
							'{fiuName}',
							global.entityName || 'the FIU'
						)}
					</Typography>
					<DenyConsent
						handleCancel={() => setOpenDrawer(false)}
						{...props}
						handleDeny={handleDeny}
						isDarkTheme={isDark}
						customDetails={props.customDetails}
						denyButtonText={t('common.buttons.yesDontRenew')}
						cancelButtonText={t('common.buttons.noGoBack')}
						isConsentRenewal={true}
					/>
				</Box>
				<Box sx={{ marginBottom: '-1rem' }}>
					<Footer {...props} />
				</Box>
			</CustomDrawer>
		</Box>
	);
};

export default ConsentRenew;
